import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getItemsbyType } from "../service/APIService";
import { Modal } from "react-bootstrap";
import {
  FaUsers,
  FaUserCheck,
  FaExclamationCircle,
  FaCheckCircle,
  FaSync,
  FaSearch,
  FaTimes,
  FaEnvelope,
  FaUtensils,
  FaFire,
  FaHeadset,
  FaCopy,
  FaCheck,
  FaArrowRight,
  FaClock,
  FaCalendarAlt,
  FaExternalLinkAlt
} from "react-icons/fa";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const [users, setUsers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [inprogressCount, setInProgressCount] = useState(0);
  const [recipeCount, setRecipeCount] = useState(0);
  const [uploadCount, setUploadCount] = useState(0);
  const [todayRecipesCount, setTodayRecipesCount] = useState(0);
  const [todayUploadsCount, setTodayUploadsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Search, filter & sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTab, setFilterTab] = useState("all"); // 'all' | 'active' | 'inactive'
  const [sortBy, setSortBy] = useState("recent"); // 'recent' | 'recipes' | 'scans' | 'name' | 'email'
  const [visibleCount, setVisibleCount] = useState(15);

  // User details modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Helper for local date string YYYY-MM-DD
  const getLocalDateString = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Time-ago formatting helper
  const formatTimeAgo = (date) => {
    if (!date) return "No activity";
    const now = new Date();
    const diffMs = now - new Date(date);
    if (diffMs < 0) return "Just now";
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay === 1) return "Yesterday";
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatFullDate = (date) => {
    if (!date) return "No activity recorded";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "Invalid date";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "U";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  // Fetch all dashboard data concurrently
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [usersRes, recipesRes, uploadsRes, contactRes] = await Promise.allSettled([
        getItemsbyType("appusers"),
        getItemsbyType("recipe-ai"),
        getItemsbyType("recipe-ai-food-calorie"),
        getItemsbyType("contactus"),
      ]);

      const rawUsers = usersRes.status === "fulfilled" && Array.isArray(usersRes.value) ? usersRes.value : [];
      const recipes = recipesRes.status === "fulfilled" && Array.isArray(recipesRes.value) ? recipesRes.value : [];
      const uploads = uploadsRes.status === "fulfilled" && Array.isArray(uploadsRes.value) ? uploadsRes.value : [];
      const contactCases = contactRes.status === "fulfilled" && Array.isArray(contactRes.value) ? contactRes.value : [];

      setRecipeCount(recipes.length);
      setUploadCount(uploads.length);

      // Support cases stats
      const pending = contactCases.filter((c) => (c.status || "").toUpperCase() === "PENDING").length;
      const completed = contactCases.filter((c) => (c.status || "").toUpperCase() === "COMPLETED").length;
      const inprogress = contactCases.filter((c) => (c.status || "").toUpperCase() === "INPROGRESS").length;

      setPendingCount(pending);
      setCompletedCount(completed);
      setInProgressCount(inprogress);

      // Today's counts
      const todayStr = getLocalDateString();
      const todayR = recipes.filter((r) => r.date && r.date.startsWith(todayStr)).length;
      const todayU = uploads.filter((u) => u.date && u.date.startsWith(todayStr)).length;
      setTodayRecipesCount(todayR);
      setTodayUploadsCount(todayU);

      // User aggregation map for activity, recipes, and scans
      const userActivityMap = {};

      const recordUserActivity = (email, dateVal, type, desc) => {
        if (!email || !dateVal) return;
        const normEmail = email.toLowerCase().trim();
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return;

        if (!userActivityMap[normEmail]) {
          userActivityMap[normEmail] = {
            recipesCount: 0,
            scansCount: 0,
            bestActivity: null,
          };
        }

        const currentBest = userActivityMap[normEmail].bestActivity;
        if (!currentBest || d > currentBest.date) {
          userActivityMap[normEmail].bestActivity = {
            date: d,
            type,
            description: desc,
          };
        }
      };

      recipes.forEach((r) => {
        if (r.email) {
          const norm = r.email.toLowerCase().trim();
          if (!userActivityMap[norm]) {
            userActivityMap[norm] = { recipesCount: 0, scansCount: 0, bestActivity: null };
          }
          userActivityMap[norm].recipesCount += 1;
          recordUserActivity(r.email, r.date, "Recipe Created", r.recipeName || "Generated Recipe");
        }
      });

      uploads.forEach((u) => {
        if (u.email) {
          const norm = u.email.toLowerCase().trim();
          if (!userActivityMap[norm]) {
            userActivityMap[norm] = { recipesCount: 0, scansCount: 0, bestActivity: null };
          }
          userActivityMap[norm].scansCount += 1;
          recordUserActivity(u.email, u.date, "Food Scanned", u.title || "Calorie Scan");
        }
      });

      contactCases.forEach((c) => {
        if (c.email) {
          recordUserActivity(c.email, c.date, "Support Ticket", c.subject || c.status);
        }
      });

      // Enrich users list
      const enrichedUsers = rawUsers.map((user) => {
        const uEmail = (user.email || "").toLowerCase().trim();
        const userStats = userActivityMap[uEmail] || { recipesCount: 0, scansCount: 0, bestActivity: null };

        // Fallback to user timestamps
        const candidateDates = [
          { date: user.lastLogin, type: "Login", desc: "User session" },
          { date: user.lastActive, type: "Active Session", desc: "App usage" },
          { date: user.updatedAt, type: "Profile Update", desc: "Profile modified" },
          { date: user.date, type: "Registration", desc: "Account created" },
          { date: user.createdAt, type: "Registration", desc: "Account created" },
        ];

        let best = userStats.bestActivity;
        candidateDates.forEach((cd) => {
          if (!cd.date) return;
          const d = new Date(cd.date);
          if (isNaN(d.getTime())) return;
          if (!best || d > best.date) {
            best = { date: d, type: cd.type, description: cd.desc };
          }
        });

        return {
          ...user,
          recipesCount: userStats.recipesCount,
          scansCount: userStats.scansCount,
          lastActivityDate: best ? best.date : null,
          lastActivityType: best ? best.type : "Registration",
          lastActivityDescription: best ? best.description : "Account registered",
        };
      });

      setUsers(enrichedUsers);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, refreshKey]);

  // Compute 30-day active threshold
  const thirtyDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);

  const activeRecentlyUsers = useMemo(() => {
    return users.filter((u) => u.lastActivityDate && new Date(u.lastActivityDate) >= thirtyDaysAgo);
  }, [users, thirtyDaysAgo]);

  // Filtered & sorted users
  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Tab filter
    if (filterTab === "active") {
      result = result.filter((u) => u.lastActivityDate && new Date(u.lastActivityDate) >= thirtyDaysAgo);
    } else if (filterTab === "inactive") {
      result = result.filter((u) => !u.lastActivityDate || new Date(u.lastActivityDate) < thirtyDaysAgo);
    }

    // Search filter
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((u) => {
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const actType = (u.lastActivityType || "").toLowerCase();
        return name.includes(term) || email.includes(term) || actType.includes(term);
      });
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "recent") {
        const timeA = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : 0;
        const timeB = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : 0;
        return timeB - timeA;
      }
      if (sortBy === "recipes") {
        return (b.recipesCount || 0) - (a.recipesCount || 0);
      }
      if (sortBy === "scans") {
        return (b.scansCount || 0) - (a.scansCount || 0);
      }
      if (sortBy === "name") {
        const nameA = (a.name || a.email || "").toLowerCase();
        const nameB = (b.name || b.email || "").toLowerCase();
        return nameA.localeCompare(nameB);
      }
      if (sortBy === "email") {
        const emailA = (a.email || "").toLowerCase();
        const emailB = (b.email || "").toLowerCase();
        return emailA.localeCompare(emailB);
      }
      return 0;
    });

    return result;
  }, [users, filterTab, searchTerm, sortBy, thirtyDaysAgo]);

  const copyEmailToClipboard = (email) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(email);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };

  return (
    <div className="container py-3 mb-5 pb-5">
      {/* ── Hero Admin Header ── */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4">
        <div>
          <span className="badge rounded-pill px-3 py-1 bg-success bg-opacity-10 text-success fw-bold" style={{ fontSize: "11px" }}>
            🛡️ Admin Control Panel
          </span>
          <h4 className="fw-bold text-dark mt-2 mb-1" style={{ letterSpacing: "-0.3px" }}>
            Recipe AI Operations
          </h4>
          <p className="text-secondary small mb-0">
            Real-time analytics across accounts, recipes, food scans, and user support.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light border rounded-pill px-3 py-1.5 d-flex align-items-center gap-1.5 shadow-xs"
            onClick={() => setRefreshKey((prev) => prev + 1)}
            disabled={loading}
          >
            <FaSync size={11} className={loading ? "spin-icon text-success" : "text-secondary"} />
            <span className="fw-semibold small">{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
          <Link
            to="/admin/userstats"
            className="btn btn-sm btn-outline-success rounded-pill px-3 py-1.5 fw-semibold d-flex align-items-center gap-1"
          >
            <span>User Stats</span>
            <FaArrowRight size={10} />
          </Link>
        </div>
      </div>

      {/* ── 4 Primary Metric Cards ── */}
      <div className="row g-3 mb-4">
        {/* Total Users */}
        <div className="col-6 col-md-3">
          <div
            className="app-card p-3 h-100 border text-start position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(255, 255, 255, 1) 100%)",
              borderColor: "rgba(34, 197, 94, 0.2)"
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-secondary small fw-bold">Total Users</span>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-success"
                style={{ width: "32px", height: "32px", background: "rgba(34, 197, 94, 0.14)" }}
              >
                <FaUsers size={14} />
              </div>
            </div>
            <h2 className="display-6 fw-bold text-dark mb-0">{users.length}</h2>
            <div className="d-flex align-items-center gap-1 mt-1">
              <FaUserCheck size={11} className="text-success" />
              <small className="text-success fw-semibold" style={{ fontSize: "11px" }}>
                {activeRecentlyUsers.length} active (30d)
              </small>
            </div>
          </div>
        </div>

        {/* Recipes Generated */}
        <div className="col-6 col-md-3">
          <Link to="/admin/userstats" className="text-decoration-none">
            <div
              className="app-card p-3 h-100 border text-start position-relative overflow-hidden cursor-pointer"
              style={{
                background: "linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(255, 255, 255, 1) 100%)",
                borderColor: "rgba(245, 158, 11, 0.2)"
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-secondary small fw-bold">Recipes Created</span>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-warning"
                  style={{ width: "32px", height: "32px", background: "rgba(245, 158, 11, 0.14)" }}
                >
                  <FaUtensils size={13} />
                </div>
              </div>
              <h2 className="display-6 fw-bold text-dark mb-0">{recipeCount}</h2>
              <div className="d-flex align-items-center gap-1 mt-1">
                <small className="text-warning fw-semibold" style={{ fontSize: "11px" }}>
                  +{todayRecipesCount} created today
                </small>
              </div>
            </div>
          </Link>
        </div>

        {/* Food Scans */}
        <div className="col-6 col-md-3">
          <Link to="/admin/userstats" className="text-decoration-none">
            <div
              className="app-card p-3 h-100 border text-start position-relative overflow-hidden cursor-pointer"
              style={{
                background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(255, 255, 255, 1) 100%)",
                borderColor: "rgba(59, 130, 246, 0.2)"
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-secondary small fw-bold">Food Scans</span>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-primary"
                  style={{ width: "32px", height: "32px", background: "rgba(59, 130, 246, 0.14)" }}
                >
                  <FaFire size={14} />
                </div>
              </div>
              <h2 className="display-6 fw-bold text-dark mb-0">{uploadCount}</h2>
              <div className="d-flex align-items-center gap-1 mt-1">
                <small className="text-primary fw-semibold" style={{ fontSize: "11px" }}>
                  +{todayUploadsCount} scanned today
                </small>
              </div>
            </div>
          </Link>
        </div>

        {/* Support Tickets */}
        <div className="col-6 col-md-3">
          <Link to="/admin/contactus" className="text-decoration-none">
            <div
              className="app-card p-3 h-100 border text-start position-relative overflow-hidden cursor-pointer"
              style={{
                background: "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(255, 255, 255, 1) 100%)",
                borderColor: "rgba(239, 68, 68, 0.2)"
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-secondary small fw-bold">Support Cases</span>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-danger"
                  style={{ width: "32px", height: "32px", background: "rgba(239, 68, 68, 0.14)" }}
                >
                  <FaHeadset size={14} />
                </div>
              </div>
              <h2 className="display-6 fw-bold text-dark mb-0">{pendingCount}</h2>
              <div className="d-flex align-items-center gap-1 mt-1">
                <small className="text-danger fw-semibold" style={{ fontSize: "11px" }}>
                  {completedCount} resolved · {inprogressCount} in-progress
                </small>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* ── Today's Pulse Summary ── */}
      <div className="app-card p-3 mb-4 border bg-white shadow-xs">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-success"
              style={{ width: "28px", height: "28px", background: "rgba(34, 197, 94, 0.15)" }}
            >
              <FaCalendarAlt size={12} />
            </div>
            <span className="fw-bold text-dark small">Today's Pulse ({getLocalDateString()})</span>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <span className="badge rounded-pill px-3 py-1.5 bg-light text-dark border fw-semibold" style={{ fontSize: "11px" }}>
              🥗 Recipes: <b className="text-success">{todayRecipesCount}</b>
            </span>
            <span className="badge rounded-pill px-3 py-1.5 bg-light text-dark border fw-semibold" style={{ fontSize: "11px" }}>
              🔥 Scans: <b className="text-primary">{todayUploadsCount}</b>
            </span>
            <span className="badge rounded-pill px-3 py-1.5 bg-light text-dark border fw-semibold" style={{ fontSize: "11px" }}>
              💬 Pending Tickets: <b className="text-danger">{pendingCount}</b>
            </span>
            <span className="badge rounded-pill px-3 py-1.5 bg-light text-dark border fw-semibold" style={{ fontSize: "11px" }}>
              ⚡ 30d Active Rate:{" "}
              <b className="text-success">
                {users.length > 0 ? `${Math.round((activeRecentlyUsers.length / users.length) * 100)}%` : "0%"}
              </b>
            </span>
          </div>
        </div>
      </div>

      {/* ── Registered Users Directory ── */}
      <div className="app-card p-3 border mb-4">
        {/* Header & Controls */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
          <div>
            <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
              <FaUsers className="text-success" size={18} /> User Activity Directory
              <span className="badge rounded-pill bg-success bg-opacity-10 text-success px-2 py-0.5" style={{ fontSize: "11px" }}>
                {filteredUsers.length}
              </span>
            </h5>
            <small className="text-secondary">
              Inspect user engagement, generated recipes, and calorie scan metrics
            </small>
          </div>

          {/* Sort Selector */}
          <div className="d-flex align-items-center gap-2">
            <small className="text-secondary text-nowrap fw-semibold" style={{ fontSize: "12px" }}>
              Sort:
            </small>
            <select
              className="form-select form-select-sm rounded-pill px-3 border shadow-xs"
              style={{ fontSize: "12px", width: "auto" }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Most Recently Active</option>
              <option value="recipes">Most Recipes Created</option>
              <option value="scans">Most Scans Performed</option>
              <option value="name">Name (A-Z)</option>
              <option value="email">Email (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Filter Tabs & Search Bar */}
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-6">
            <div className="tab-group-container shadow-xs d-flex">
              {[
                { key: "all", label: `All (${users.length})` },
                { key: "active", label: `Active (${activeRecentlyUsers.length})` },
                { key: "inactive", label: `Inactive (${Math.max(0, users.length - activeRecentlyUsers.length)})` },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`tab-btn ${filterTab === tab.key ? "active" : ""}`}
                  style={{
                    fontSize: "0.82rem",
                    padding: "7px 0",
                    background: filterTab === tab.key ? "#ffffff" : "transparent",
                    border: filterTab === tab.key ? "1.5px solid #111827" : "1.5px solid transparent",
                    color: filterTab === tab.key ? "#111827" : "#334155",
                    fontWeight: filterTab === tab.key ? 600 : 500,
                    borderRadius: "9999px",
                    boxShadow: filterTab === tab.key ? "0 1px 3px rgba(0,0,0,0.05)" : "none"
                  }}
                  onClick={() => setFilterTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="recipe-search-box" style={{ height: "42px", borderRadius: "9999px" }}>
              <div className="search-icon">
                <FaSearch size={13} className="text-muted" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search user by name, email..."
                style={{ fontSize: "13px" }}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-muted p-0 me-1"
                  onClick={() => setSearchTerm("")}
                >
                  <FaTimes size={12} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Users List */}
        {loading && users.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin text-success fa-2x mb-2"></i>
            <p className="text-secondary small mb-0">Loading user activity directory...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-secondary small mb-0">No users found matching your filter criteria.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {filteredUsers.slice(0, visibleCount).map((user, idx) => {
              const isUserActive = user.lastActivityDate && new Date(user.lastActivityDate) >= thirtyDaysAgo;

              return (
                <div
                  key={user.id || user.email || idx}
                  className="p-3 rounded-4 border bg-white d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 hover-shadow-sm transition-all"
                  style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                  onClick={() => setSelectedUser(user)}
                >
                  {/* Left: Avatar & Info */}
                  <div className="d-flex align-items-center gap-3 min-w-0">
                    {user.profilePic ? (
                      <img
                        src={user.profilePic}
                        alt="Profile"
                        className="rounded-circle shadow-xs flex-shrink-0"
                        width="42"
                        height="42"
                        style={{ objectFit: "cover", border: "1.5px solid #22c55e" }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-xs flex-shrink-0"
                        style={{
                          width: "42px",
                          height: "42px",
                          background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                          fontSize: "14px"
                        }}
                      >
                        {getInitials(user.name || user.email)}
                      </div>
                    )}

                    <div className="min-w-0">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold text-dark small text-truncate">
                          {user.name || (user.email ? user.email.split("@")[0] : "User")}
                        </span>
                        {isUserActive ? (
                          <span className="badge rounded-pill bg-success bg-opacity-10 text-success px-2 py-0.5" style={{ fontSize: "9px" }}>
                            Active
                          </span>
                        ) : (
                          <span className="badge rounded-pill bg-secondary bg-opacity-10 text-secondary px-2 py-0.5" style={{ fontSize: "9px" }}>
                            Inactive
                          </span>
                        )}
                      </div>
                      <small className="text-secondary d-block text-truncate" style={{ fontSize: "11px" }}>
                        {user.email || "No email"}
                      </small>
                    </div>
                  </div>

                  {/* Right: Metrics & Last Activity */}
                  <div className="d-flex align-items-center justify-content-between justify-content-sm-end gap-3 flex-shrink-0">
                    <div className="text-sm-end">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge rounded-pill px-2.5 py-1 bg-light text-dark border" style={{ fontSize: "11px" }}>
                          🥗 {user.recipesCount || 0}
                        </span>
                        <span className="badge rounded-pill px-2.5 py-1 bg-light text-dark border" style={{ fontSize: "11px" }}>
                          🔥 {user.scansCount || 0}
                        </span>
                      </div>
                      <small className="text-muted d-block mt-1" style={{ fontSize: "10px" }}>
                        <FaClock size={9} className="me-1" />
                        {user.lastActivityDate ? formatTimeAgo(user.lastActivityDate) : "Never"}
                      </small>
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm btn-light rounded-circle shadow-xs border p-0 d-flex align-items-center justify-content-center"
                      style={{ width: "30px", height: "30px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUser(user);
                      }}
                      title="Inspect user"
                    >
                      <FaArrowRight size={11} className="text-secondary" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Load More Button */}
            {filteredUsers.length > visibleCount && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-outline-success rounded-pill px-4 py-2 small fw-semibold shadow-xs"
                  onClick={() => setVisibleCount((prev) => prev + 15)}
                >
                  Show More Users ({filteredUsers.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── User Details Modal ── */}
      <Modal show={!!selectedUser} onHide={() => setSelectedUser(null)} centered>
        {selectedUser && (
          <>
            <div className="modal-header border-bottom">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-xs"
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                    fontSize: "13px"
                  }}
                >
                  {getInitials(selectedUser.name || selectedUser.email)}
                </div>
                <div>
                  <h6 className="modal-title fw-bold text-dark mb-0">
                    {selectedUser.name || "User Details"}
                  </h6>
                  <small className="text-secondary" style={{ fontSize: "11px" }}>
                    ID: {selectedUser.id ? selectedUser.id.substring(0, 12) + "..." : "N/A"}
                  </small>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={() => setSelectedUser(null)} />
            </div>

            <Modal.Body className="py-3">
              {/* Email Row with Copy */}
              <div className="d-flex align-items-center justify-content-between p-2.5 rounded-3 bg-light border mb-3">
                <div className="d-flex align-items-center gap-2 min-w-0">
                  <FaEnvelope size={13} className="text-success flex-shrink-0" />
                  <span className="small fw-semibold text-dark text-truncate">
                    {selectedUser.email || "No email"}
                  </span>
                </div>
                {selectedUser.email && (
                  <button
                    type="button"
                    className="btn btn-sm btn-white border rounded-pill px-2.5 py-0.5 d-flex align-items-center gap-1 shadow-xs"
                    style={{ fontSize: "11px" }}
                    onClick={() => copyEmailToClipboard(selectedUser.email)}
                  >
                    {copiedEmail ? <FaCheck size={10} className="text-success" /> : <FaCopy size={10} />}
                    <span>{copiedEmail ? "Copied" : "Copy"}</span>
                  </button>
                )}
              </div>

              {/* Engagement Stats Grid */}
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div className="p-2.5 rounded-3 border bg-light text-center">
                    <small className="text-secondary d-block fw-semibold" style={{ fontSize: "11px" }}>
                      🥗 Recipes Generated
                    </small>
                    <span className="fs-5 fw-bold text-dark">{selectedUser.recipesCount || 0}</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-2.5 rounded-3 border bg-light text-center">
                    <small className="text-secondary d-block fw-semibold" style={{ fontSize: "11px" }}>
                      🔥 Calorie Scans
                    </small>
                    <span className="fs-5 fw-bold text-dark">{selectedUser.scansCount || 0}</span>
                  </div>
                </div>
              </div>

              {/* Activity Timeline Details */}
              <div className="border rounded-3 p-3 bg-white">
                <h6 className="fw-bold text-dark small mb-2">Activity Timeline</h6>
                <div className="d-flex justify-content-between py-1.5 border-bottom">
                  <span className="text-secondary small">Last Active:</span>
                  <span className="small fw-semibold text-dark">{formatFullDate(selectedUser.lastActivityDate)}</span>
                </div>
                <div className="d-flex justify-content-between py-1.5 border-bottom">
                  <span className="text-secondary small">Activity Type:</span>
                  <span className="badge rounded-pill bg-success bg-opacity-10 text-success small">
                    {selectedUser.lastActivityType || "App Usage"}
                  </span>
                </div>
                <div className="d-flex justify-content-between py-1.5 border-bottom">
                  <span className="text-secondary small">Details:</span>
                  <span className="small text-muted text-truncate" style={{ maxWidth: "200px" }}>
                    {selectedUser.lastActivityDescription || "Active session"}
                  </span>
                </div>
                <div className="d-flex justify-content-between pt-1.5">
                  <span className="text-secondary small">Registered On:</span>
                  <span className="small text-dark">{formatFullDate(selectedUser.date || selectedUser.createdAt)}</span>
                </div>
              </div>
            </Modal.Body>

            <div className="modal-footer border-top d-flex justify-content-between">
              {selectedUser.email ? (
                <a
                  href={`mailto:${selectedUser.email}`}
                  className="btn btn-sm btn-outline-success rounded-pill px-3 d-flex align-items-center gap-1.5"
                >
                  <FaEnvelope size={11} />
                  <span>Send Email</span>
                </a>
              ) : (
                <div></div>
              )}
              <button
                type="button"
                className="btn btn-sm btn-secondary rounded-pill px-3"
                onClick={() => setSelectedUser(null)}
              >
                Close
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
